import AWS from 'aws-sdk'
import { S3Client } from '@aws-sdk/client-s3';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert, showConfirmDialog } from '../utils/sweetAlertConfig';


const MySwal = withReactContent(Swal);

let s3Client = null;
try {
  if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
    console.error('AWS credentials are not configured. Please check your environment variables.');
  } else {
    s3Client = new S3Client({
      region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
      credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      },
    });
  }
} catch (error) {
  console.error('Error initializing AWS S3 client:', error);
}
export { s3Client };


const s3 = new AWS.S3({
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
})


export const uploadToS3 = async (s3_path, file_type, file_body) => {
  try {
    // Upload image to S3
    if (file_body) {
      const imageUploadParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
        Key: `${s3_path}`, // Adjust extension based on the image format
        Body: file_body,
        ContentType: file_type,
      }
      await s3.upload(imageUploadParams).promise()
    }
  } catch (error) {
    console.error('Error uploading to S3:', error)
    // alert('Failed to upload files to S3.')

    showErrorAlert('Failed to upload files to S3. Please try again.');
  }
}

// Function to generate pre-signed URL for a given image
export const generatePresignedUrl = async (imagePath) => {
  // Add timestamp to prevent caching
  const timestamp = new Date().getTime();
  const downloadImageName = `${timestamp}_${imagePath.split('/').pop()}`;
  const params = {
    Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
    Key: imagePath,
    Expires: 3600, // 1 hour
    ResponseContentDisposition: `attachment; filename="${downloadImageName}"`,
  };

  try {
    return await s3.getSignedUrlPromise('getObject', params);
  } catch (error) {
    console.error('Error generating pre-signed URL:', error);
    return null;
  }
};

export async function readFileFromS3(PromptFilepath) {
  const params = {
    Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
    Key: PromptFilepath,
  };

  try {
    const data = await s3.getObject(params).promise();
    const fileContent = data.Body.toString('utf-8'); // Convert buffer to string
    return fileContent;
  } catch (error) {
    console.error("Error fetching the file from S3:", error);
    return null;
  }
}

export async function listFoldersFromS3(path) {
  const params = {
    Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
    Prefix: path,
    Delimiter: '/'
  };

  try {
    const data = await s3.listObjectsV2(params).promise();
    // Extract folder names from CommonPrefixes
    const folders = data.CommonPrefixes ? data.CommonPrefixes.map(prefix => {
      const folderPath = prefix.Prefix;
      return folderPath.substring(path.length, folderPath.length - 1);
    }) : [];

    return folders;
  } catch (error) {
    console.error("Error listing folders from S3:", error);
    return [];
  }
}

export async function listFileNamesFromS3(path) {
  const params = {
    Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
    Prefix: path,
    Delimiter: '/'
  };

  try {
    const data = await s3.listObjectsV2(params).promise();
    return data.Contents.map(file => file.Key);
  } catch (error) {
    console.error("Error listing files from S3:", error);
    return [];
  }
}






export const moveS3Folder = async (oldPath, newPath) => {
  try {
    // S3 doesn't have a direct "move folder" operation
    // We need to list all objects in the folder, copy them to the new location, then delete the originals

    // List all objects in the folder
    const listParams = {
      Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
      Prefix: oldPath
    };

    const listedObjects = await s3.listObjectsV2(listParams).promise();

    if (listedObjects.Contents.length === 0) {
      // console.log('No objects found to move');
      return false;
    }

    // Copy each object to the new location
    const copyPromises = listedObjects.Contents.map(object => {
      const oldKey = object.Key;
      // Create the new key by replacing the old path prefix with the new path
      const newKey = oldKey.replace(oldPath, newPath);

      const copyParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
        CopySource: `${process.env.REACT_APP_AWS_BUCKET_NAME}/${oldKey}`,
        Key: newKey
      };

      return s3.copyObject(copyParams).promise();
    });

    await Promise.all(copyPromises);

    // Delete the original objects
    const deletePromises = listedObjects.Contents.map(object => {
      const deleteParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
        Key: object.Key
      };

      return s3.deleteObject(deleteParams).promise();
    });

    await Promise.all(deletePromises);

    // console.log('Folder moved successfully!');
    showSuccessAlert('Folder moved successfully');
    return true;
  } catch (error) {
    console.error('Error moving folder:', error);
    showErrorAlert('Failed to move folder');
    return false;
  }
};

export const deleteFromS3 = async (s3_path) => {
  try {
    // console.log('Attempting to delete from path:', s3_path);

    // First, list all objects in the folder
    const listParams = {
      Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
      Prefix: s3_path
    };

    // console.log('Listing objects with params:', listParams);
    const listedObjects = await s3.listObjectsV2(listParams).promise();
    // console.log('Found objects:', listedObjects.Contents);

    if (!listedObjects.Contents || listedObjects.Contents.length === 0) {
      // console.log('No objects found to delete');
      return false;
    }

    // Delete all objects in the folder
    const deletePromises = listedObjects.Contents.map(object => {
      const deleteParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
        Key: object.Key
      };
      // console.log('Deleting object:', deleteParams);
      return s3.deleteObject(deleteParams).promise();
    });

    await Promise.all(deletePromises);
    // console.log('All files in folder deleted successfully from S3');
    return true;
  } catch (error) {
    console.error('Detailed error deleting files from S3:', {
      message: error.message,
      code: error.code,
      statusCode: error.statusCode,
      path: s3_path
    });
    return false;
  }
};

export const copyBaseUserDataTemplate = async (user_name) => {
  try {
    // First check if the destination folder already exists
    const destCheckParams = {
      Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
      Prefix: `USERS/${user_name}/`,
      MaxKeys: 1
    };
    

    // List all objects in source prefix
    const listParams = {
      Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
      Prefix: 'USERS/default_user/'
    };
    
    const listedObjects = await s3.listObjectsV2(listParams).promise();
    if (!listedObjects.Contents || listedObjects.Contents.length === 0) {
      // console.log("No template files found to copy");
      return { 
        status: false, 
        message: "No template files found to copy" 
      };
    }

    // console.log(`Starting to copy ${listedObjects.Contents.length} files for user ${user_name}...`);
    
    // Create an array to store all copy operations
    const copyPromises = listedObjects.Contents.map(async (object, index) => {
      const sourceKey = object.Key;
      const destKey = sourceKey.replace('USERS/default_user/', `USERS/${user_name}/`);
      const copyParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
        CopySource: `${process.env.REACT_APP_AWS_BUCKET_NAME}/${encodeURIComponent(sourceKey)}`,
        Key: destKey
      };
      
      try {
        // console.log(`Copying file ${index + 1}/${listedObjects.Contents.length}: ${sourceKey}`);
        await s3.copyObject(copyParams).promise();
        // console.log(`Successfully copied ${sourceKey}`);
        return { success: true, key: sourceKey };
      } catch (error) {
        console.error(`Failed to copy ${sourceKey}:`, error);
        return { 
          success: false, 
          key: sourceKey, 
          error: error.message || 'Unknown error' 
        };
      }
    });

    // Wait for all copy operations to complete
    // console.log("Waiting for all copy operations to complete...");
    const results = await Promise.all(copyPromises);
    
   
    // console.log(`Successfully copied all ${results.length} files for user: ${user_name}`);
    return { 
      status: true, 
      message: "Template files copied successfully",
      filesCopied: results.length
    };

  } catch (error) {
    console.error("Error copying template files:", error);
    return { 
      status: false, 
      message: `Failed to copy template files: ${error.message}`,
      error: error
    };
  }
};

export const extractKeyFromPresignedUrl = (url) => {
  const match = url.match(/\.amazonaws\.com\/(.+?)\?/);
  return match ? decodeURIComponent(match[1]) : null;
}

export const downloadImageLocallyFromS3 = async (key, format) => {
  try {
    const params = {
      Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
      Key: key
    };

    const data = await s3.getObject(params).promise();

    // Create a blob from the binary data
    const blob = new Blob([data.Body], { type: data.ContentType || 'image/jpg' });

    // Create a download link
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;

    // Extract filename from the key and apply the requested format
    let filename = key.split('/').pop() || `image-${Date.now()}`;

    // Remove any existing extension and add the requested format
    filename = filename.replace(/\.[^/.]+$/, ""); // Remove extension if exists
    a.download = `${filename}.${format.toLowerCase()}`;

    // Trigger download
    document.body.appendChild(a);
    a.click();

    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);

    return true;
  } catch (error) {
    console.error('Error downloading image:', error);
    return false;
  }
};


export const lowQualityToHighQuality = async (lowPresignedUrl) => {
  if (lowPresignedUrl.includes('lowQuality')) {
    const key = extractKeyFromPresignedUrl(lowPresignedUrl);
    const key2 = key.replace('lowQuality', 'highQuality');
    const highPresignedPath = key2.replace('.webp', '.png');
    const highPresignedUrl = await generatePresignedUrl(highPresignedPath);
    return highPresignedUrl.toString();
  } else {
    return lowPresignedUrl;
  }
}


export async function deleteFolderFromS3(path) {
  try {
    const params = {
      Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
      Prefix: path
    };

    const listedObjects = await s3.listObjectsV2(params).promise();
    const deletePromises = listedObjects.Contents.map(object => {
      const deleteParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET_NAME,
        Key: object.Key
      };
      return s3.deleteObject(deleteParams).promise();
    });
    await Promise.all(deletePromises);
    // console.log('All files in folder deleted successfully from S3');
    return true;
  } catch (error) {
    console.error('Error deleting folder from S3:', error);
    return false;
  }
}